import { fmtMoneda } from '../../utils/moneda';
import React, { useState, useRef, useEffect } from 'react';
import { supabase, insertRow } from '../../db/engine';
import { reaplicarSacoPoliza } from '../cobros/Cobros';
import { generarComisionesDelCobro } from '../../db/comisiones';
import { Upload, X, AlertTriangle, FileText, HelpCircle, Send, Sparkles } from 'lucide-react';
import { fmtFecha } from '../../utils/fecha';
import { getAbrev } from '../../utils/marca';
import { fileADatosIA } from '../../utils/docIA';   // PDF/imagen → base64 para la IA
import { authHeaders } from '../../utils/apiIA';
import { agruparVigencias, elegirVigencia, claveNumero } from '../../utils/vigencias';
import { loadXLSX } from '../../utils/xlsx';

// Carga SheetJS desde CDN

const fmt$ = fmtMoneda;

// ASSA usa dd/mm/yyyy → ISO yyyy-mm-dd
function parseFechaDDMMYYYY(val) {
  if (!val) return null;
  const m = String(val).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

// Parser del Estado de Cuenta de ASSA (Honorarios Profesionales)
function parseASSA(rows) {
  const pagos = [];
  const meta = { aseguradora: 'ASSA', fechaPagoRU: null, documento: null, agente: null, hasta: null };
  let enCheques = false;

  for (const row of rows) {
    const cells = (row || []).map(c => (c == null ? '' : String(c).trim()));
    const noVacias = cells.filter(c => c !== '');
    if (!noVacias.length) continue;
    const joined = noVacias.join(' ');

    // --- Encabezado ---
    if (joined.includes('Hasta el:')) {
      meta.hasta = joined.replace(/.*Hasta el:\s*/, '').trim();
    }
    const idxAgente = noVacias.findIndex(c => c.toUpperCase().includes('NOMBRE/AGENTE'));
    if (idxAgente >= 0 && noVacias[idxAgente + 1]) {
      meta.agente = noVacias[idxAgente + 1];
    }

    // --- Sección "Cheques o ACH" (cómo ASSA pagó a RU) ---
    if (joined.includes('Cheques o ACH')) { enCheques = true; continue; }
    if (enCheques) {
      // Salta la fila de títulos (Fecha / No. Obligación / Documento / Comisión Pagada)
      if (joined.toUpperCase().includes('OBLIGACI')) continue;
      const f = parseFechaDDMMYYYY(noVacias[0]);
      if (f) {
        meta.fechaPagoRU = f;
        // Documento = el valor numérico (recibo/cheque), evitando el código tipo "CHQ"
        const doc = noVacias.slice(1).find(c => /^\d+$/.test(c.replace(/[^\d]/g, '')) && c.replace(/[^\d]/g, '').length >= 3);
        meta.documento = doc ? doc.replace(/[^\d]/g, '') : (noVacias[2] || noVacias[1] || null);
      }
      continue;
    }

    // --- Filas de comisión (COMI) ---
    if (noVacias[0].toUpperCase() === 'COMI') {
      const fecha     = parseFechaDDMMYYYY(noVacias[1]);
      const poliza    = noVacias[2] || '';
      const asegurado = noVacias[5] || '';
      const prima     = parseFloat(String(noVacias[6]).replace(/,/g, '')) || 0;
      const tasa      = parseFloat(String(noVacias[9]).replace(/,/g, '')) || 0;
      const comision  = parseFloat(String(noVacias[10]).replace(/,/g, '')) || 0;
      pagos.push({ fecha, poliza, asegurado, prima, tasa, comision, _raw: noVacias });
    }
  }
  return { pagos, meta };
}

// SURA: período en M/D/YYYY (formato US) → ISO yyyy-mm-dd
function parseFechaMDYYYY(val) {
  const m = String(val == null ? '' : val).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
}

// SURA: fechas del detalle en YYYYMMDD → ISO yyyy-mm-dd
function parseFechaYYYYMMDD(val) {
  const s = String(val == null ? '' : val).trim().replace(/\D/g, '');
  const m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (!m) return null;
  return `${m[1]}-${m[2]}-${m[3]}`;
}

// Monto que puede venir como $1,234.56, (445.81) [negativo] o -445.81
function parseMoneySURA(val) {
  let s = String(val == null ? '' : val).trim();
  if (!s) return 0;
  const neg = /^\(.*\)$/.test(s) || /-/.test(s);
  s = s.replace(/[()$\s,]/g, '').replace(/-/g, '');
  const n = parseFloat(s);
  if (isNaN(n)) return 0;
  return neg ? -n : n;
}

// Parser del Estado de Cuenta de SURA (Honorarios Profesionales — Corretaje).
// El archivo está organizado por RAMO: cada sección ("Detalle de Honorarios
// Corretaje - Automóvil / Responsabilidad Civil / Vida Individual …") trae su
// propia fila de encabezado. Detectamos las columnas por sus títulos (robusto
// ante cambios de posición) y leemos las filas de datos hasta el "Total".
function parseSURA(rows) {
  const pagos = [];
  const meta = { aseguradora: 'SURA', fechaPagoRU: null, documento: null, agente: null, hasta: null, periodo: null };
  let cols = null;        // columnas detectadas del encabezado de la sección actual
  let ramoActual = null;

  for (const row of rows) {
    const cells = (row || []).map(c => (c == null ? '' : String(c).trim()));
    const noVacias = cells.filter(c => c !== '');
    if (!noVacias.length) continue;
    const joined = noVacias.join(' ');

    // Período de la planilla → fecha en que SURA liquidó a RU (fin del período)
    if (/Per[ií]odo\s+de\s+la\s+Planilla/i.test(joined)) {
      const mr = joined.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s*-\s*(\d{1,2}\/\d{1,2}\/\d{4})/);
      if (mr) {
        meta.periodo = `${mr[1]} - ${mr[2]}`;
        meta.hasta = mr[2];
        meta.fechaPagoRU = parseFechaMDYYYY(mr[2]);
        meta.documento = (meta.fechaPagoRU || '').replace(/-/g, '') || null;
      }
      continue;
    }
    // Corredor (si viene en el archivo)
    const idxCorr = cells.findIndex(c => /^Corredor:?$/i.test(c));
    if (idxCorr >= 0) {
      const val = cells.slice(idxCorr + 1).find(c => c !== '');
      if (val) meta.agente = val;
    }

    // Título de sección por ramo → reinicia la detección de columnas
    const mt = joined.match(/Detalle de Honorarios Corretaje\s*[-–]\s*(.+)/i);
    if (mt) { ramoActual = mt[1].trim(); cols = null; continue; }

    // Fila de encabezado de la sección (Póliza + Asegurado + Comisión)
    const tienePoliza = cells.some(c => /^P[oó]liza$/i.test(c));
    const tieneAseg   = cells.some(c => /Asegurado/i.test(c));
    const tieneComis  = cells.some(c => /Comisi[oó]n/i.test(c));
    if (tienePoliza && tieneAseg && tieneComis) {
      cols = {
        poliza:    cells.findIndex(c => /^P[oó]liza$/i.test(c)),
        asegurado: cells.findIndex(c => /Asegurado/i.test(c)),
        fecha:     cells.findIndex(c => /^Fecha$/i.test(c)),
        recaudo:   cells.findIndex(c => /Recaudo/i.test(c)),
        recibo:    cells.findIndex(c => /Recibo/i.test(c)),
        pct:       cells.findIndex(c => /^%$/.test(c)),
        comision:  cells.findIndex(c => /Comisi[oó]n/i.test(c)),
        neto:      cells.findIndex(c => /Neto/i.test(c)),
      };
      continue;
    }

    // Fin de sección: fila "Total …" o resumen "Recaudo (Sin ITBMS)"
    if (cols && (/^Total\b/i.test(joined) || /Recaudo\s*\(Sin\s*ITBMS\)/i.test(joined))) {
      cols = null;
      continue;
    }

    // Fila de datos
    if (cols && cols.poliza >= 0) {
      const poliza = (cells[cols.poliza] || '').trim();
      const fecha  = parseFechaYYYYMMDD(cols.fecha >= 0 ? cells[cols.fecha] : '');
      // Requiere póliza + fecha YYYYMMDD válida para tratarla como fila de datos
      if (poliza && fecha) {
        const prima     = parseMoneySURA(cols.recaudo  >= 0 ? cells[cols.recaudo]  : 0);
        const comision  = parseMoneySURA(cols.comision >= 0 ? cells[cols.comision] : (cols.neto >= 0 ? cells[cols.neto] : 0));
        // El % puede venir como "20.00%" (texto) o 0.2 (fracción cruda de Excel)
        let tasa = cols.pct >= 0 ? (parseFloat(String(cells[cols.pct]).replace(/[^\d.]/g, '')) || 0) : 0;
        if (tasa > 0 && tasa <= 1) tasa = tasa * 100;
        const recibo    = cols.recibo    >= 0 ? (cells[cols.recibo]    || '').trim() : '';
        const asegurado = cols.asegurado >= 0 ? (cells[cols.asegurado] || '').trim() : '';
        pagos.push({ fecha, poliza, asegurado, prima, tasa, comision, recibo, ramo: ramoActual, _raw: noVacias });
      }
    }
  }
  return { pagos, meta };
}

// Concilia cada pago del estado de cuenta contra los cobros del sistema.
// OJO: un mismo NÚMERO de póliza puede tener VARIAS vigencias (renovación con el
// mismo número). Hay que mirarlas TODAS: buscar el cobro en cualquiera de ellas y
// declarar "saldo cero" solo si NINGUNA tiene cuotas pendientes. Quedarse con una
// sola vigencia hacía que una póliza ya renovada saliera como "renovar" y que un
// pago ya aplicado en la vigencia nueva saliera como "falta" (riesgo de duplicarlo).
// El número se compara por `claveNumero` (sin espacios/guiones): cada aseguradora
// lo escribe a su manera ("02 03 2345" en el reporte vs "02-03-2345" en el sistema).
// Por eso se traen las pólizas de la ASEGURADORA del reporte (paginado) además de
// las coincidencias exactas, y se emparejan en memoria.
async function polizasCandidatas(numeros, aseguradoraId) {
  const cols = 'id, numero, fecha_inicio, fecha_vencimiento, estado';
  const porId = {};
  const { data: exactas } = await supabase.from('polizas').select(cols).in('numero', numeros);
  (exactas || []).forEach(p => { porId[p.id] = p; });
  if (aseguradoraId) {
    const PAG = 1000;
    for (let desde = 0; ; desde += PAG) {
      const { data } = await supabase.from('polizas').select(cols)
        .eq('aseguradora_id', aseguradoraId).order('id').range(desde, desde + PAG - 1);
      (data || []).forEach(p => { porId[p.id] = p; });
      if (!data || data.length < PAG) break;
    }
  }
  return Object.values(porId);
}

async function conciliar(pagos, aseguradoraId) {
  const numeros = [...new Set(pagos.map(p => String(p.poliza ?? '').trim()).filter(Boolean))];
  let polMap = {};          // claveNumero -> [vigencias] (de la más vieja a la más nueva)
  let cobros = [];
  let pendMap = {};         // poliza_id -> # de cuotas pendientes/parciales
  if (numeros.length) {
    const claves = new Set(numeros.map(claveNumero));
    const polizas = (await polizasCandidatas(numeros, aseguradoraId))
      .filter(p => claves.has(claveNumero(p.numero)));
    polMap = agruparVigencias(polizas, claveNumero);
    const polizaIds = (polizas || []).map(p => p.id);
    if (polizaIds.length) {
      const { data } = await supabase.from('cobros_recibidos')
        .select('id, poliza_id, fecha, monto, anulado')
        .in('poliza_id', polizaIds);
      cobros = (data || []).filter(c => !c.anulado);
      // Cuántas cuotas pendientes/parciales tiene cada VIGENCIA (para detectar saldo cero)
      const { data: cuotas } = await supabase.from('pagos')
        .select('poliza_id, estado').in('poliza_id', polizaIds);
      (cuotas || []).forEach(c => {
        if (c.estado === 'pendiente' || c.estado === 'parcial')
          pendMap[c.poliza_id] = (pendMap[c.poliza_id] || 0) + 1;
      });
    }
  }
  const filas = pagos.map(p => {
    const vigs = polMap[claveNumero(p.poliza)] || [];
    if (!vigs.length) return { ...p, polizaId: null, estado: 'sin_poliza', saldada: false, nota: null, cobroMatch: null };
    const ids = vigs.map(v => v.id);
    // Match por fecha cercana (±3 días) contra los cobros de CUALQUIER vigencia
    const match = cobros.find(c => {
      if (!ids.includes(c.poliza_id) || !c.fecha || !p.fecha) return false;
      const diff = Math.abs((new Date(c.fecha) - new Date(p.fecha)) / 86400000);
      return diff <= 3;
    });
    const estado = match ? 'aplicado' : 'falta';
    // Si ya está aplicado manda la vigencia donde está el cobro; si falta, se elige
    // la vigencia que le toca (la que cubre la fecha o la que tiene saldo).
    const pol = match
      ? (vigs.find(v => v.id === match.poliza_id) || vigs[vigs.length - 1])
      : elegirVigencia(vigs, p.fecha, pendMap);
    const polizaId = pol.id;
    // ¿Póliza en saldo cero? Solo si NINGUNA vigencia del número tiene cuotas
    // pendientes. Solo importa cuando "falta": el cobro se registra pero queda SIN
    // aplicar — hay que renovar la póliza para que el pago caiga en la nueva vigencia.
    const sinSaldo = vigs.every(v => (pendMap[v.id] || 0) === 0);
    const saldada = estado === 'falta' && Number(p.prima) > 0 && sinSaldo;
    const nota = saldada
      ? `Póliza en saldo cero (vigencia ${pol.fecha_inicio || '?'} → ${pol.fecha_vencimiento || '?'}${vigs.length > 1 ? `, ${vigs.length} vigencias revisadas` : ''}). El cobro se registra pero queda PENDIENTE sin aplicar. Renueva la póliza y el pago se aplicará solo a la nueva vigencia.`
      : null;
    return { ...p, polizaId, estado, saldada, nota, _numSistema: pol.numero, _vigIni: pol.fecha_inicio || '', _vigFin: pol.fecha_vencimiento || '', _vigs: vigs.length, cobroMatch: match || null };
  });

  // Comisiones de los cobros de estas pólizas (sin las anuladas), en bloques para
  // no armar una URL gigante.
  const pendAseg = {};      // cobro_id -> { n, esperado, recibido } (filas en 'pendiente_aseg')
  const conComision = new Set();   // cobros que tienen alguna comisión viva
  const todosIds = cobros.map(c => c.id);
  for (let i = 0; i < todosIds.length; i += 150) {
    const { data: coms } = await supabase.from('comisiones')
      .select('cobro_id, estado, monto_agente, monto_ru, monto_recibido_aseg')
      .in('cobro_id', todosIds.slice(i, i + 150)).neq('estado', 'anulada');
    (coms || []).forEach(c => {
      conComision.add(c.cobro_id);
      if (c.estado !== 'pendiente_aseg') return;
      const a = pendAseg[c.cobro_id] || (pendAseg[c.cobro_id] = { n: 0, esperado: 0, recibido: 0 });
      a.n++;
      a.esperado += Number(c.monto_agente || 0) + Number(c.monto_ru || 0);
      a.recibido += Number(c.monto_recibido_aseg || 0);
    });
  }

  // Cobros de la póliza que NINGUNA fila del reporte emparejó y cuya comisión no
  // se ha cobrado a la aseguradora (o ni siquiera tiene comisión). Si una fila sale
  // "falta" y la póliza tiene uno de estos, lo más probable es que sea EL MISMO pago
  // registrado con otra fecha: aplicarlo lo duplicaría. Se muestran para que se
  // corrija el cobro en Cobros y, al volver a cargar el reporte, haga match.
  const emparejados = new Set(filas.filter(f => f.cobroMatch).map(f => f.cobroMatch.id));
  const sueltos = cobros.filter(c => !emparejados.has(c.id) && (!conComision.has(c.id) || pendAseg[c.id]));

  return filas.map(f => {
    // Pagos que YA estaban aplicados: su comisión puede seguir esperando a la
    // aseguradora; este reporte es justo el que dice que pagó → se ofrecen para marcarla.
    const pa = f.cobroMatch && pendAseg[f.cobroMatch.id];
    if (pa) return { ...f, comPendAseg: pa };
    if (f.estado !== 'falta') return f;
    const ids = (polMap[claveNumero(f.poliza)] || []).map(v => v.id);
    // Solo cobros cercanos (±60 días): uno de hace meses es otro pago, no este.
    const cerca = c => !f.fecha || !c.fecha || Math.abs((new Date(c.fecha) - new Date(f.fecha)) / 86400000) <= 60;
    const revisar = sueltos.filter(c => ids.includes(c.poliza_id) && cerca(c))
      .map(c => ({ id: c.id, fecha: c.fecha, monto: c.monto, sinComision: !conComision.has(c.id) }))
      .sort((a, b) => (a.fecha || '').localeCompare(b.fecha || ''));
    // Con posibles duplicados, la fila arranca DESMARCADA: hay que revisar primero.
    return revisar.length ? { ...f, revisar, sel: false } : f;
  });
}

// Marca que la aseguradora pagó la comisión de un cobro: reparte lo recibido entre
// sus filas en 'pendiente_aseg' (agente + referido…), SUMÁNDOLO a lo que ya se
// hubiera recibido antes. Si con eso se completa lo esperado, pasan a "Cobrado de
// Aseguradoras" (estado 'pendiente' + fecha); si no, el saldo queda en seguimiento.
async function marcarPagoAseguradora(cobroId, recibido, fechaAseg) {
  const { data: comsCobro } = await supabase.from('comisiones')
    .select('id, monto_agente, monto_ru, monto_recibido_aseg')
    .eq('cobro_id', cobroId).eq('estado', 'pendiente_aseg');
  const filas = comsCobro || [];
  if (!filas.length) return 0;
  const esperado = filas.reduce((s, c) => s + Number(c.monto_agente || 0) + Number(c.monto_ru || 0), 0);
  const total = filas.reduce((s, c) => s + Number(c.monto_recibido_aseg || 0), 0) + (Number(recibido) || 0);
  const completo = total >= esperado - 0.005;
  for (const c of filas) {
    const espC = Number(c.monto_agente || 0) + Number(c.monto_ru || 0);
    const parte = completo ? espC : (esperado > 0 ? Math.round(total * espC / esperado * 100) / 100 : 0);
    const { error } = await supabase.from('comisiones')
      .update({
        ...(completo ? { estado: 'pendiente', fecha_recibio_comision: fechaAseg } : {}),
        monto_recibido_aseg: parte,
      })
      .eq('id', c.id);
    if (error) throw error;
  }
  return filas.length;
}

// Aplica los pagos que faltan: por cada uno registra cobro + saco + comisión.
// `modo` decide si además se marca que la ASEGURADORA ya pagó esa comisión:
//   'pago_aseg'    → el reporte viene con un pago recibido de la aseguradora:
//                    la comisión pasa a "Cobrado de Aseguradoras" y se guarda
//                    cuánto se recibió de verdad (`monto_recibido_aseg`), de modo
//                    que si pagaron de menos el saldo siga en seguimiento.
//   'solo_aplicar' → solo se registran los pagos del cliente que faltaban; la
//                    comisión QUEDA esperando a la aseguradora (pendiente_aseg).
async function aplicarFaltantes(pagosFalta, meta, modo = 'pago_aseg', pagosMarcar = []) {
  const aseg = meta.aseguradora || 'ASEG';
  const numRecibo = `${aseg}-EC-${meta.documento || 'SD'}`;
  const nota = `Aplicado desde Estado de Cuenta de Comisiones ${aseg} — ${meta.fechaPagoRU || ''} — Doc ${meta.documento || ''}`.trim();
  // Impuesto de cada póliza
  const polizaIds = [...new Set(pagosFalta.map(p => p.polizaId).filter(Boolean))];
  const impMap = {};
  if (polizaIds.length) {
    const { data } = await supabase.from('polizas').select('id, impuesto').in('id', polizaIds);
    (data || []).forEach(p => { impMap[p.id] = (p.impuesto == null ? 7 : parseFloat(p.impuesto)); });
  }
  let aplicados = 0; const errores = [];
  for (const p of pagosFalta) {
    if (!p.polizaId) continue;
    try {
      const imp = impMap[p.polizaId] ?? 7;
      const montoConImp = Math.round((Number(p.prima) || 0) * (1 + imp / 100) * 100) / 100;
      // Si la póliza está en saldo cero, el cobro queda registrado pero sin aplicar
      // (el saco no tiene cuota pendiente). Se deja traza en la nota.
      const notaPago = p.saldada
        ? `${nota} | PÓLIZA EN SALDO CERO — cobro pendiente de aplicar; renovar la póliza para que caiga en la nueva vigencia.`
        : nota;
      // 1) Cobro (fecha de la fila, monto con impuesto, trazado al estado de cuenta).
      //    Si la fila trae su propio recibo de la aseguradora (ej. SURA "PC968760"),
      //    se usa ese; si no, el recibo del estado de cuenta (ej. ASSA-EC-doc).
      const cobro = await insertRow('cobros_recibidos', {
        poliza_id: p.polizaId,
        fecha: p.fecha,
        monto: montoConImp,
        donde_pago: 'Aseguradora',
        num_recibo_aseg: p.recibo || numRecibo,
        notas: notaPago,
      });
      // 2) Saco: aplica el cobro a las cuotas
      await reaplicarSacoPoliza(p.polizaId);
      // 3) Comisión por el cobro
      await generarComisionesDelCobro(cobro.id, p.polizaId, montoConImp);
      // 4) Solo si el reporte trae un PAGO recibido de la aseguradora se marca la
      //    comisión como cobrada de aseguradora. En modo "solo aplicar" se deja en
      //    'pendiente_aseg' (el cliente pagó, pero la aseguradora aún no nos pagó).
      if (modo === 'pago_aseg') {
        const fechaAseg = meta.fechaPagoRU || p.fecha || new Date().toISOString().slice(0, 10);
        // Lo que la aseguradora dice haber pagado por esta póliza (puede ser menos
        // que lo esperado; el saldo queda en seguimiento).
        await marcarPagoAseguradora(cobro.id, Number(p.recibido ?? p.comision) || 0, fechaAseg);
      }
      aplicados++;
    } catch (e) {
      errores.push(`${p.poliza}: ${e.message || e}`);
    }
  }
  // Pagos que ya estaban aplicados y cuya comisión seguía esperando a la
  // aseguradora: no se registra cobro nuevo, solo se marca la comisión.
  let marcados = 0;
  if (modo === 'pago_aseg') {
    for (const p of (pagosMarcar || [])) {
      try {
        const fechaAseg = meta.fechaPagoRU || p.fecha || new Date().toISOString().slice(0, 10);
        if (await marcarPagoAseguradora(p.cobroMatch.id, Number(p.recibido ?? p.comision) || 0, fechaAseg)) marcados++;
      } catch (e) {
        errores.push(`${p.poliza}: ${e.message || e}`);
      }
    }
  }
  return { aplicados, marcados, errores };
}

// === REGISTRO DE FORMATOS POR ASEGURADORA ===
// Para sumar una aseguradora nueva: agrega una entrada con su detector (match) y su lector (parse).
// tipo 'excel' usa SheetJS (parser propio). tipo 'pdf' usará Claude API (a futuro).
const PARSERS = [
  { key: 'ASSA', match: (n) => /ASSA/i.test(n || ''), parse: parseASSA, tipo: 'excel', accept: '.xls,.xlsx', ejemplo: 'EstadoDeCuentaCorredores.xls' },
  // \bSURA\b evita falsos positivos con "InSURAnce" (International Insurance, Pan-American Life Insurance)
  { key: 'SURA', match: (n) => /\bSURA\b|suramericana/i.test(n || ''), parse: parseSURA, tipo: 'excel', accept: '.xls,.xlsx', ejemplo: 'Planilla de Corredores.xls' },
  // Próximas: { key:'REGIONAL', match:(n)=>/REGIONAL/i.test(n), parse: parseRegional, tipo:'pdf', accept:'.pdf', ejemplo:'Regional.pdf' },
];
function getParser(nombreAseg) {
  return PARSERS.find(p => p.match(nombreAseg)) || null;
}

export default function ImportarEstadoCuenta({ onClose }) {
  const abrev = getAbrev();
  const [pagos, setPagos] = useState([]);
  const [meta, setMeta]   = useState(null);
  const [fechaCobro, setFechaCobro] = useState(''); // fecha en que la aseguradora pagó a RU (obligatoria)
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState('');
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [aseguradoras, setAseguradoras] = useState([]);
  const [asegSel, setAsegSel] = useState(''); // nombre de la aseguradora elegida
  const [aplicando, setAplicando] = useState(false);
  const [confirmAplicar, setConfirmAplicar] = useState(false);
  const [resultado, setResultado] = useState(null);
  // Paso obligatorio antes de aplicar: ¿este reporte trae un PAGO de la aseguradora
  // o solo sirve para aplicar los pagos del cliente que faltan? Ver aplicarFaltantes.
  const [modo, setModo] = useState('');
  const [ia, setIa] = useState(null);          // revisión de la IA (duplicados/alertas)
  const [iaCargando, setIaCargando] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    supabase.from('aseguradoras').select('id, nombre').order('nombre')
      .then(({ data }) => setAseguradoras(data || []));
  }, []);

  const parser = getParser(asegSel);
  const asegId = aseguradoras.find(a => a.nombre === asegSel)?.id || null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!asegSel) { setError('Primero selecciona la aseguradora.'); return; }
    // Ya no se exige un lector de Excel programado: si no lo hay (o el archivo es
    // PDF), lo lee la IA.
    setProcesando(true); setError(''); setPagos([]); setMeta(null); setNombreArchivo(file.name);
    try {
      let parsed, m;
      const esPDF = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      if (esPDF || !parser) {
        // Lo lee la IA. Cubre PDF/imagen de cualquier aseguradora, y también el
        // EXCEL de una aseguradora sin lector programado: en ese caso la hoja se
        // convierte a texto aquí, porque Claude no entiende un .xls como documento.
        const esExcel = /\.(xls|xlsx|csv)$/i.test(file.name);
        let cuerpo;
        if (esExcel) {
          const XLSX = await loadXLSX();
          const buf  = await file.arrayBuffer();
          const wb   = XLSX.read(buf, { type: 'array', cellDates: false });
          const ws   = wb.Sheets[wb.SheetNames[0]];
          const filas = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
          const texto = filas
            .map(f => (f || []).map(c => (c == null ? '' : String(c).trim())).join(' | '))
            .filter(l => l.replace(/[|\s]/g, '') !== '')
            .join('\n');
          cuerpo = { texto, aseguradora: asegSel };
        } else {
          const { media_type, data } = await fileADatosIA(file);
          cuerpo = { media_type, data, aseguradora: asegSel };
        }
        const r = await fetch('/api/leer-estado-cuenta', {
          method: 'POST', headers: await authHeaders(),
          body: JSON.stringify(cuerpo),
        });
        const out = await r.json().catch(() => ({}));
        if (!out.ok) {
          const motivos = {
            no_configurado: 'La lectura con IA no está activada en tu sistema.',
            cupo: out.detalle || 'Llegaste al límite de análisis con IA de tu plan este mes.',
            sin_json: 'No se pudo interpretar el documento. ¿Es el estado de cuenta correcto?',
          };
          setError(motivos[out.reason] || ('No se pudo leer el documento.' + (out.detalle ? ` (${out.detalle})` : '')));
          setProcesando(false);
          return;
        }
        parsed = out.pagos; m = { aseguradora: asegSel, ...(out.meta || {}) };
      } else {
        const XLSX = await loadXLSX();
        const buf  = await file.arrayBuffer();
        const wb   = XLSX.read(buf, { type: 'array', cellDates: false });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        ({ pagos: parsed, meta: m } = parser.parse(rows));
      }
      if (!parsed || !parsed.length) {
        setError('No se encontraron pagos en el archivo. ¿Es el estado de cuenta correcto para esta aseguradora?');
        setProcesando(false);
        return;
      }
      const conciliados = await conciliar(parsed, asegId);
      setPagos(conciliados);
      setMeta(m || { aseguradora: asegSel });
      // Pre-llena la fecha de pago de la aseguradora si el documento la trae; si no,
      // queda vacía y el usuario debe ponerla antes de aplicar.
      setFechaCobro(m?.fechaPagoRU || '');
    } catch (err) {
      setError(err.message || 'Error al leer el archivo.');
    }
    setProcesando(false);
  };

  // Solo se pueden aplicar las filas con recaudo POSITIVO. Las negativas (endosos/
  // reversos, ej. SURA con montos entre paréntesis) se marcan para revisión manual.
  const esAplicable = (p) => p.estado === 'falta' && !!p.polizaId && Number(p.prima) > 0;
  // Ya aplicado, pero su comisión sigue esperando a la aseguradora: con un reporte
  // de PAGO se marca como cobrada (sin registrar otro cobro).
  const esMarcable  = (p) => modo === 'pago_aseg' && p.estado === 'aplicado' && !!p.cobroMatch && !!p.comPendAseg;
  const esSeleccionable = (p) => esAplicable(p) || esMarcable(p);
  const aplicables  = pagos.filter(esAplicable);
  const marcables   = pagos.filter(esMarcable);
  // De las aplicables se registran SOLO las marcadas (`sel`, marcadas por defecto).
  // La persona decide fila por fila: por ejemplo dejar fuera una de saldo cero hasta
  // renovar la póliza, o una que la aseguradora reportó de más.
  const pagosFalta   = aplicables.filter(p => p.sel !== false);
  const pagosMarcar  = marcables.filter(p => p.sel !== false);
  const pagosRevisar = pagos.filter(p => p.estado === 'falta' && p.polizaId && Number(p.prima) <= 0);
  const sumFaltaComision = [...pagosFalta, ...pagosMarcar].reduce((s, p) => s + Number(p.comision || 0), 0);
  const saldadasAAplicar = pagosFalta.filter(p => p.saldada).length;
  const seleccionables   = aplicables.length + marcables.length;
  const seleccionadas    = pagosFalta.length + pagosMarcar.length;
  const todasMarcadas    = seleccionables > 0 && seleccionadas === seleccionables;
  const totPorMarcar     = pagos.filter(p => p.estado === 'aplicado' && p.comPendAseg).length;

  const marcarFila  = (idx, val) => setPagos(ps => ps.map((p, i) => i === idx ? { ...p, sel: val } : p));
  const marcarTodas = (val) => setPagos(ps => ps.map(p => esSeleccionable(p) ? { ...p, sel: val } : p));

  // Edita cuánto se recibió de verdad por una fila (si la aseguradora pagó de menos,
  // el saldo queda en seguimiento). Solo aplica en modo "pago de aseguradora".
  const setRecibido = (idx, val) => {
    setPagos(ps => ps.map((p, i) => i === idx ? { ...p, recibido: val === '' ? '' : Number(val) } : p));
  };

  // Revisión con IA: detecta duplicados y filas dudosas antes de aplicar.
  const revisarConIA = async () => {
    setIaCargando(true); setIa(null);
    try {
      const r = await fetch('/api/conciliar-comisiones', {
        method: 'POST', headers: await authHeaders(),
        body: JSON.stringify({
          aseguradora: meta?.aseguradora || asegSel,
          documento: meta?.documento || null,
          fechaPagoAseg: fechaCobro || null,
          filas: pagos.map((p, i) => ({
            i, fecha: p.fecha, poliza: p.poliza, asegurado: p.asegurado,
            prima: p.prima, comision: p.comision, estado: p.estado,
            saldada: !!p.saldada,
            // Si esta fila entra o no al botón "Aplicar los que faltan" (mismo criterio
            // que `pagosFalta`). Sin esto la IA alertaba de "duplicado" por filas que el
            // sistema NUNCA vuelve a aplicar, y eso frenaba a quien concilia.
            se_aplicara: p.estado === 'falta' && !!p.polizaId && Number(p.prima) > 0,
            cobro_sistema: p.cobroMatch ? { fecha: p.cobroMatch.fecha, monto: p.cobroMatch.monto } : null,
          })),
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'No se pudo revisar con IA');
      setIa(j);
    } catch (e) { setIa({ error: e.message || 'Error de IA' }); }
    setIaCargando(false);
  };

  const handleAplicar = async () => {
    if (modo === 'pago_aseg' && !fechaCobro) { alert(`Pon la fecha en que la aseguradora pagó a ${abrev} antes de aplicar.`); return; }
    setAplicando(true); setResultado(null);
    try {
      // La fecha que confirma el usuario manda (autollenada del documento o puesta a mano).
      const metaConFecha = { ...(meta || { aseguradora: asegSel }), fechaPagoRU: fechaCobro };
      const { aplicados, marcados, errores } = await aplicarFaltantes(pagosFalta, metaConFecha, modo, pagosMarcar);
      // Re-conciliar para refrescar los estados (los aplicados pasan a ✅)
      const limpio = pagos.map(p => ({ fecha: p.fecha, poliza: p.poliza, asegurado: p.asegurado, prima: p.prima, tasa: p.tasa, comision: p.comision, recibo: p.recibo, ramo: p.ramo }));
      const reconc = await conciliar(limpio, asegId);
      setPagos(reconc);
      setConfirmAplicar(false);
      setResultado({ aplicados, marcados, errores });
    } catch (e) {
      alert('Error al aplicar: ' + (e.message || ''));
    }
    setAplicando(false);
  };

  const totApl     = pagos.filter(p => p.estado === 'aplicado').length;
  const totFal     = pagos.filter(p => p.estado === 'falta' && !p.saldada && !p.revisar).length;
  const filasRevisar = pagos.filter(p => p.revisar);
  const totSaldada = pagos.filter(p => p.saldada).length;
  const totSinP    = pagos.filter(p => p.estado === 'sin_poliza').length;
  const sumComision = pagos.reduce((s, p) => s + Number(p.comision || 0), 0);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" style={{ maxWidth: 980 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Importar Estado de Cuenta de Comisiones</div>
            <div style={{ fontSize: 12, color: 'var(--silver)', marginTop: 2 }}>
              Elige la aseguradora y sube su estado de cuenta — el sistema lee los pagos y los compara con tus cobros (no aplica nada todavía)
            </div>
          </div>
          <button className="btn btn-icon btn-secondary" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Subir archivo */}
        {!pagos.length && (
          <div style={{ padding: '20px 8px' }}>
            {/* Paso 1: elegir aseguradora */}
            <div style={{ maxWidth: 440, margin: '0 auto 16px' }}>
              <label style={{ fontSize: 12, color: 'var(--silver)', fontWeight: 600, display: 'block', marginBottom: 6 }}>1. Aseguradora</label>
              <select className="form-select" style={{ width: '100%' }} value={asegSel}
                onChange={e => { setAsegSel(e.target.value); setError(''); }}>
                <option value="">— Selecciona la aseguradora —</option>
                {aseguradoras.map(a => (
                  <option key={a.id} value={a.nombre}>{a.nombre}</option>
                ))}
              </select>
            </div>

            {/* Paso 2: subir archivo — Excel con lector propio, o PDF/imagen con IA */}
            <input ref={fileRef} type="file" accept=".xls,.xlsx,.pdf,.png,.jpg,.jpeg" style={{ display: 'none' }} onChange={handleFile} />
            <div style={{ border: '2px dashed rgba(var(--sky-rgb),0.4)', borderRadius: 12, padding: '28px 20px', background: 'rgba(var(--steel-rgb),0.3)', textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
              <FileText size={40} style={{ color: 'var(--sky-light)', marginBottom: 10 }} />
              <div style={{ fontSize: 14, color: 'var(--silver)', marginBottom: 6 }}>
                {asegSel
                  ? <>2. Sube el estado de cuenta de <b>{asegSel}</b>{parser?.ejemplo ? <> (ej. <b>{parser.ejemplo}</b>)</> : null}</>
                  : 'Primero elige la aseguradora'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--silver)', marginBottom: 14 }}>
                {parser
                  ? <>Excel de <b>{asegSel}</b> se lee directo. Si te lo mandaron en <b>PDF</b>, súbelo igual: lo lee la IA.</>
                  : <>Súbelo en <b>PDF</b>, <b>Excel</b> o imagen — <b>lo lee la IA</b> y luego revisas cada fila antes de aplicar.</>}
              </div>
              <button className="btn btn-primary" disabled={procesando || !asegSel} onClick={() => fileRef.current?.click()}>
                <Upload size={14} /> {procesando ? 'Leyendo...' : 'Seleccionar archivo'}
              </button>
            </div>
            {error && (
              <div style={{ marginTop: 14, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto', padding: '10px 14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, color: 'var(--text-danger)', fontSize: 13 }}>
                <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />{error}
              </div>
            )}
          </div>
        )}

        {/* Resultado */}
        {pagos.length > 0 && (
          <>
            {/* Encabezado del estado de cuenta */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, padding: '12px 14px', background: 'rgba(var(--steel-rgb),0.4)', borderRadius: 10, marginBottom: 12, fontSize: 12 }}>
              <div><span style={{ color: 'var(--silver)' }}>Aseguradora:</span> <b style={{ color: 'var(--silver)' }}>{meta?.aseguradora || '—'}</b></div>
              <div><span style={{ color: 'var(--silver)' }}>Agente:</span> <b style={{ color: 'var(--silver)' }}>{meta?.agente || '—'}</b></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: 'var(--silver)' }}>Aseg. pagó a {abrev} *:</span>
                <input type="date" value={fechaCobro} onChange={e => setFechaCobro(e.target.value)}
                  className="form-input" style={{ width: 150, padding: '4px 8px', height: 28,
                    borderColor: fechaCobro ? undefined : '#ef4444' }}
                  title={`Fecha en que la aseguradora le pagó a ${abrev} (obligatoria)`} />
                {!fechaCobro && <span style={{ color: 'var(--text-danger)', fontSize: 11 }}>requerida</span>}
              </div>
              <div><span style={{ color: 'var(--silver)' }}>Documento:</span> <b style={{ color: 'var(--silver)' }}>{meta?.documento || '—'}</b></div>
              <div><span style={{ color: 'var(--silver)' }}>Archivo:</span> <b style={{ color: 'var(--silver)' }}>{nombreArchivo}</b></div>
            </div>

            {/* PASO: ¿qué es este reporte? Decide si se marca que la aseguradora pagó. */}
            <div style={{ padding: '12px 14px', background: 'rgba(var(--sky-rgb),0.06)', border: `1px solid ${modo ? 'rgba(var(--sky-rgb),0.25)' : 'rgba(245, 158, 11, 0.5)'}`, borderRadius: 10, marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--sky-light)', letterSpacing: .5, marginBottom: 8 }}>
                ¿QUÉ VAS A HACER CON ESTE REPORTE? {!modo && <span style={{ color: 'var(--text-warning)' }}>— elige una opción</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
                {[
                  { key: 'pago_aseg', t: `La aseguradora ya me pagó estas comisiones`,
                    d: `Registra los pagos que falten Y marca las comisiones como cobradas de la aseguradora. Puedes editar cuánto se recibió por póliza.` },
                  { key: 'solo_aplicar', t: 'Solo aplicar los pagos pendientes',
                    d: 'Registra en el sistema los pagos del cliente que faltaban. Las comisiones QUEDAN esperando a la aseguradora.' },
                ].map(o => (
                  <label key={o.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    background: modo === o.key ? 'rgba(var(--sky-rgb),0.16)' : 'rgba(var(--steel-rgb),0.35)',
                    border: `1px solid ${modo === o.key ? 'rgba(var(--sky-rgb),0.6)' : 'rgba(var(--silver-rgb),0.2)'}` }}>
                    <input type="radio" name="modo_ec" checked={modo === o.key} onChange={() => setModo(o.key)} style={{ marginTop: 3 }} />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{o.t}</span>
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--silver)', marginTop: 2, lineHeight: 1.5 }}>{o.d}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Resumen de conciliación */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ padding: '8px 16px', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-success)' }}>{totApl}</span>
                <span style={{ fontSize: 12, color: 'var(--silver)', marginLeft: 6 }}>ya aplicados</span>
              </div>
              {totPorMarcar > 0 && (
                <div style={{ padding: '8px 16px', background: 'rgba(var(--sky-rgb),0.1)', border: '1px solid rgba(var(--sky-rgb),0.35)', borderRadius: 8 }}
                  title="El pago del cliente ya estaba registrado, pero su comisión sigue esperando a la aseguradora">
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--sky-light)' }}>{totPorMarcar}</span>
                  <span style={{ fontSize: 12, color: 'var(--silver)', marginLeft: 6 }}>comisión esperando aseguradora</span>
                </div>
              )}
              <div style={{ padding: '8px 16px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-warning)' }}>{totFal}</span>
                <span style={{ fontSize: 12, color: 'var(--silver)', marginLeft: 6 }}>faltan aplicar</span>
              </div>
              {filasRevisar.length > 0 && (
                <div style={{ padding: '8px 16px', background: 'rgba(245, 158, 11, 0.18)', border: '1px solid rgba(245, 158, 11, 0.5)', borderRadius: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-warning)' }}>{filasRevisar.length}</span>
                  <span style={{ fontSize: 12, color: 'var(--silver)', marginLeft: 6 }}>revisar cobro</span>
                </div>
              )}
              {totSaldada > 0 && (
                <div style={{ padding: '8px 16px', background: 'rgba(168, 85, 247, 0.12)', border: '1px solid rgba(168, 85, 247, 0.35)', borderRadius: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-purple)' }}>{totSaldada}</span>
                  <span style={{ fontSize: 12, color: 'var(--silver)', marginLeft: 6 }}>saldo cero (renovar)</span>
                </div>
              )}
              {totSinP > 0 && (
                <div style={{ padding: '8px 16px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-danger)' }}>{totSinP}</span>
                  <span style={{ fontSize: 12, color: 'var(--silver)', marginLeft: 6 }}>póliza no encontrada</span>
                </div>
              )}
              <div style={{ padding: '8px 16px', background: 'rgba(var(--steel-rgb),0.5)', border: '1px solid rgba(var(--sky-rgb),0.3)', borderRadius: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--silver)' }}>Total comisión doc:</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-success)', marginLeft: 6 }}>{fmt$(sumComision)}</span>
              </div>
            </div>

            {/* Tabla de pagos */}
            <div className="table-wrapper" style={{ maxHeight: 380, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 34, textAlign: 'center' }}
                      title="Marca cuáles se van a aplicar">
                      <input type="checkbox" checked={todasMarcadas} disabled={!seleccionables}
                        onChange={e => marcarTodas(e.target.checked)}
                        style={{ accentColor: 'var(--sky)', width: 15, height: 15, cursor: seleccionables ? 'pointer' : 'default' }} />
                    </th>
                    <th>Estado</th><th>Fecha</th><th>Póliza</th><th>Asegurado</th>
                    <th style={{ textAlign: 'right' }}>Prima</th><th style={{ textAlign: 'center' }}>Tasa</th>
                    <th style={{ textAlign: 'right' }}>Comisión</th>
                    {modo === 'pago_aseg' && <th style={{ textAlign: 'right' }} title="Cuánto se recibió de verdad por esta póliza. Si es menor, el saldo queda en seguimiento.">Recibido</th>}
                    <th>Cobro en sistema</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((p, i) => {
                    const badge = p.revisar
                      ? { txt: '🔎 Revisar cobro', color: 'var(--text-warning)', bg: 'rgba(245, 158, 11, 0.18)' }
                      : p.saldada
                      ? { txt: '⏳ Saldo cero', color: 'var(--text-purple)', bg: 'rgba(168, 85, 247, 0.14)' }
                      : p.estado === 'aplicado' && p.comPendAseg
                      ? { txt: '✅ Aplicado · comisión por cobrar', color: 'var(--sky-light)', bg: 'rgba(var(--sky-rgb),0.12)' }
                      : p.estado === 'aplicado'
                      ? { txt: '✅ Aplicado', color: 'var(--text-success)', bg: 'rgba(34, 197, 94, 0.12)' }
                      : p.estado === 'falta'
                        ? { txt: '⚠️ Falta', color: 'var(--text-warning)', bg: 'rgba(245, 158, 11, 0.12)' }
                        : { txt: '⛔ Sin póliza', color: 'var(--text-danger)', bg: 'rgba(239, 68, 68, 0.12)' };
                    const puede = esSeleccionable(p);
                    const marcada = puede && p.sel !== false;
                    return (
                      <tr key={i} style={puede && !marcada ? { opacity: 0.45 } : undefined}>
                        <td style={{ textAlign: 'center' }}>
                          {puede ? (
                            <input type="checkbox" checked={marcada}
                              onChange={e => marcarFila(i, e.target.checked)}
                              style={{ accentColor: 'var(--sky)', width: 15, height: 15, cursor: 'pointer' }}
                              title="Aplicar esta fila" />
                          ) : null}
                        </td>
                        <td><span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700, color: badge.color, background: badge.bg }}>{badge.txt}</span></td>
                        <td style={{ fontSize: 12 }}>{fmtFecha(p.fecha) || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--sky-light)', fontWeight: 600 }}>
                          {p.poliza}
                          {/* El reporte lo escribe distinto (ej. con espacios): se muestra cómo está en el sistema */}
                          {p._numSistema && String(p._numSistema).trim() !== String(p.poliza ?? '').trim() && (
                            <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--silver)' }}
                              title="El número del reporte se emparejó con esta póliza del sistema">
                              = {p._numSistema}
                            </div>
                          )}
                          {/* Con varias vigencias del mismo número, se muestra a cuál se está apuntando */}
                          {p._vigs > 1 && (
                            <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--silver)' }}
                              title={`Este número tiene ${p._vigs} vigencias. El pago se ubicó en la que se muestra.`}>
                              {p._vigs} vig. · usa {fmtFecha(p._vigIni)}→{fmtFecha(p._vigFin)}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: 11 }}>{p.asegurado}</td>
                        <td style={{ textAlign: 'right', fontSize: 12 }}>{fmt$(p.prima)}</td>
                        <td style={{ textAlign: 'center', fontSize: 12 }}>{p.tasa ? p.tasa + '%' : '—'}</td>
                        <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-success)', fontWeight: 700 }}>{fmt$(p.comision)}</td>
                        {modo === 'pago_aseg' && (
                          <td style={{ textAlign: 'right' }}>
                            {puede ? (
                              <input type="number" step="0.01" className="form-input"
                                style={{ width: 92, padding: '3px 6px', height: 26, fontSize: 12, textAlign: 'right' }}
                                value={p.recibido ?? p.comision ?? ''}
                                onChange={e => setRecibido(i, e.target.value)}
                                title="Monto realmente recibido de la aseguradora por esta póliza" />
                            ) : <span style={{ fontSize: 11, color: 'var(--silver)' }}>—</span>}
                          </td>
                        )}
                        <td style={{ fontSize: 11, color: p.saldada ? '#c084fc' : 'var(--silver)' }}>
                          {p.revisar ? (
                            <span style={{ color: 'var(--text-warning)' }}
                              title="Cobros de esta póliza que el reporte no emparejó y cuya comisión no se ha cobrado a la aseguradora. Puede ser este mismo pago registrado con otra fecha.">
                              Sin comisión cobrada: {p.revisar.map(c => `${fmtFecha(c.fecha)} · ${fmt$(c.monto)}${c.sinComision ? ' (sin comisión)' : ''}`).join(' | ')}
                            </span>
                          ) : p.saldada
                            ? `Saldo cero · vig. ${fmtFecha(p._vigIni)}→${fmtFecha(p._vigFin)} — queda pendiente; renovar la póliza`
                            : (p.cobroMatch ? `${fmtFecha(p.cobroMatch.fecha)} · ${fmt$(p.cobroMatch.monto)}` : '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(var(--sky-rgb),0.08)', border: '1px solid rgba(var(--sky-rgb),0.25)', borderRadius: 8, fontSize: 12, color: 'var(--silver)' }}>
              <HelpCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6, color: 'var(--sky-light)' }} />
              Revisa que las pólizas, fechas y montos se leyeron bien. Los <b style={{ color: 'var(--text-success)' }}>✅ Aplicado</b> ya están en el sistema. Los <b style={{ color: 'var(--text-warning)' }}>⚠️ Falta</b> los puedes aplicar con el botón de abajo (registra el cobro, genera la comisión y marca que la aseguradora pagó a {abrev}). <b>Usa la casilla de cada fila</b> para escoger cuáles se aplican: las desmarcadas se quedan como están.
            </div>

            {filasRevisar.length > 0 && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.45)', borderRadius: 8, fontSize: 12, color: 'var(--silver)' }}>
                <div style={{ fontWeight: 700, color: 'var(--text-warning)', marginBottom: 6 }}>
                  🔎 Revisar los pagos que no tienen comisión cobrada ({filasRevisar.length})
                </div>
                <div style={{ lineHeight: 1.6, marginBottom: 6 }}>
                  El reporte no encontró estos pagos con su fecha (±3 días), pero la póliza tiene cobros cercanos (±60 días) cuya comisión <b>no se ha cobrado a la aseguradora</b>. Lo más probable es que sea <b>el mismo pago registrado con otra fecha o monto</b>: si lo aplicas desde aquí quedaría <b>duplicado</b>. Por eso estas filas vienen <b>desmarcadas</b>.
                  {' '}<b>Qué hacer:</b> ve a <b>Cobros</b>, busca la póliza y corrige la fecha del cobro (botón <b>Editar</b> del cobro; lo hace un master) para que coincida con la del reporte; luego vuelve a cargar el reporte y hará match. Si de verdad es otro pago distinto, marca la fila y aplícala.
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                  {filasRevisar.map((p, k) => (
                    <li key={k}>
                      <b style={{ color: 'var(--sky-light)' }}>{p._numSistema || p.poliza}</b>{p.asegurado ? ` (${p.asegurado})` : ''} — en el reporte: {fmtFecha(p.fecha)} · prima {fmt$(p.prima)}
                      {' → '}en el sistema: {p.revisar.map(c => `${fmtFecha(c.fecha)} · ${fmt$(c.monto)}${c.sinComision ? ' (sin comisión)' : ''}`).join(' | ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {totSaldada > 0 && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.35)', borderRadius: 8, fontSize: 12, color: '#d8b4fe' }}>
                <span style={{ marginRight: 6 }}>⏳</span>
                Hay <b>{totSaldada}</b> pago(s) de pólizas en <b>saldo cero</b>. Si los aplicas se registran (queda el cobro y la comisión), pero el cobro queda <b>pendiente sin aplicar</b> porque la vigencia ya está pagada. <b>Acción del ejecutivo:</b> buscar y <b>renovar</b> esa póliza — al renovarla, el pago se aplica solo a la nueva vigencia. Si prefieres esperar a renovarla, <b>desmarca esa fila</b> y aplica el resto.
              </div>
            )}

            {pagosRevisar.length > 0 && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 8, fontSize: 12, color: 'var(--text-danger)' }}>
                <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Hay <b>{pagosRevisar.length}</b> fila(s) con recaudo <b>negativo</b> (endosos/reversos). No se aplican automáticamente — revísalas y ajústalas a mano si corresponde.
              </div>
            )}

            {/* Revisión de la IA: duplicados y alertas antes de aplicar */}
            {ia && (
              <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: 12,
                background: ia.error ? 'rgba(239, 68, 68, 0.08)' : 'rgba(var(--sky-rgb),0.08)',
                border: `1px solid ${ia.error ? 'rgba(239, 68, 68, 0.3)' : 'rgba(var(--sky-rgb),0.3)'}`,
                color: ia.error ? 'var(--text-danger)' : 'var(--silver)' }}>
                <div style={{ fontWeight: 700, color: ia.error ? 'var(--text-danger)' : 'var(--sky-light)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Sparkles size={13} /> Revisión con IA
                </div>
                {ia.error ? ia.error : (
                  <>
                    {ia.resumen && <div style={{ marginBottom: 6, lineHeight: 1.6 }}>{ia.resumen}</div>}
                    {Array.isArray(ia.alertas) && ia.alertas.length > 0 && (
                      <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                        {ia.alertas.map((a, k) => (
                          <li key={k} style={{ color: a.nivel === 'alto' ? 'var(--text-danger)' : a.nivel === 'medio' ? 'var(--text-warning)' : 'var(--silver)' }}>
                            {a.poliza ? <b>{a.poliza}: </b> : null}{a.mensaje}
                          </li>
                        ))}
                      </ul>
                    )}
                    {(!ia.alertas || !ia.alertas.length) && !ia.resumen && <span>Sin observaciones.</span>}
                  </>
                )}
              </div>
            )}

            {resultado && (
              <div style={{ marginTop: 10, padding: '10px 14px', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: 8, fontSize: 13, color: '#86efac' }}>
                ✅ {resultado.aplicados} pago(s) aplicado(s) correctamente.
                {resultado.marcados ? ` ${resultado.marcados} comisión(es) de pagos ya aplicados marcadas como cobradas de la aseguradora.` : ''}
                {resultado.errores?.length ? <span style={{ color: 'var(--text-danger)' }}> ⚠️ {resultado.errores.length} con error: {resultado.errores.join('; ')}</span> : null}
              </div>
            )}
            <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button className="btn btn-secondary" onClick={() => { setPagos([]); setMeta(null); setError(''); setResultado(null); setFechaCobro(''); setModo(''); setIa(null); }}>Cargar otro</button>
              <button className="btn btn-secondary" onClick={revisarConIA} disabled={iaCargando}
                title="La IA revisa duplicados y filas dudosas antes de aplicar">
                <Sparkles size={14} /> {iaCargando ? 'Revisando…' : 'Revisar con IA'}
              </button>
              {seleccionables > 0 && (() => {
                const sinMarcar = seleccionadas === 0;
                const falta = sinMarcar || !modo || (modo === 'pago_aseg' && !fechaCobro);
                const motivo = sinMarcar ? 'Marca al menos una fila para aplicar'
                  : !modo ? 'Elige primero qué vas a hacer con este reporte'
                  : 'Falta la fecha de pago de la aseguradora';
                return (
                  <button className="btn btn-success" onClick={() => { if (falta) { alert(motivo); return; } setConfirmAplicar(true); }}
                    disabled={falta} style={falta ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    title={falta ? motivo : undefined}>
                    <Send size={14} /> {modo === 'solo_aplicar' ? `Aplicar pagos pendientes (${pagosFalta.length})` : `Aplicar (${seleccionadas})`}
                  </button>
                );
              })()}
              <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
            </div>
          </>
        )}
      </div>

      {/* Confirmación de aplicar */}
      {confirmAplicar && (
        <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); if (!aplicando) setConfirmAplicar(false); }}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Aplicar pagos faltantes</div></div>
            <div style={{ padding: '8px 4px' }}>
              <p style={{ fontSize: 13, color: 'var(--silver)', lineHeight: 1.6 }}>
                Se van a aplicar <b>{pagosFalta.length}</b> pago(s) del estado de cuenta de <b>{meta?.aseguradora || asegSel}</b> (comisión total {fmt$(sumFaltaComision)}).{pagosFalta.length > 0 && ' Por cada uno:'}
              </p>
              {pagosFalta.length > 0 && (
              <ul style={{ fontSize: 12, color: 'var(--silver)', lineHeight: 1.7, marginTop: 8, paddingLeft: 18 }}>
                <li>Registra el <b>cobro</b> (fecha de la fila + impuesto de la póliza)</li>
                <li>Aplica el <b>saco</b> y genera la <b>comisión</b></li>
                {modo === 'pago_aseg' ? (
                  <li>Marca <b>"Aseg. pagó a {abrev}"</b> ({fmtFecha(fechaCobro) || '—'}) → pasa a <b>"Cobrado de Aseguradoras"</b>. Si se recibió <b>menos</b> de lo esperado, el saldo queda <b>pendiente de la aseguradora</b> para seguimiento.</li>
                ) : (
                  <li>La comisión <b>queda esperando a la aseguradora</b> (no se marca como cobrada).</li>
                )}
                <li>Recibo: <b>{(meta?.aseguradora || asegSel)}-EC-{meta?.documento || '—'}</b></li>
              </ul>
              )}
              {pagosMarcar.length > 0 && (
                <p style={{ fontSize: 12, color: 'var(--silver)', marginTop: 10, lineHeight: 1.6 }}>
                  Además, <b>{pagosMarcar.length}</b> pago(s) que <b>ya estaban aplicados</b>: no se registra otro cobro, solo se marca que la aseguradora pagó su comisión ({fmtFecha(fechaCobro) || '—'}) → pasan a <b>"Cobrado de Aseguradoras"</b>.
                </p>
              )}
              {saldadasAAplicar > 0 && (
                <p style={{ fontSize: 12, color: '#d8b4fe', marginTop: 10, lineHeight: 1.6 }}>
                  ⏳ De esos, <b>{saldadasAAplicar}</b> son de pólizas en <b>saldo cero</b>: el cobro queda registrado pero <b>pendiente sin aplicar</b> hasta que renueves la póliza. Si no quieres registrarlos todavía, cancela y desmárcalos.
                </p>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button className="btn btn-secondary" onClick={() => setConfirmAplicar(false)} disabled={aplicando}>Cancelar</button>
              <button className="btn btn-success" onClick={handleAplicar} disabled={aplicando}>
                <Send size={14} /> {aplicando ? 'Aplicando...' : `Aplicar ${seleccionadas}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
